/**
 * The single query-string builder for hand-written API service modules.
 *
 * It iterates the params object instead of naming each field, so a param added
 * to a request interface reaches the wire by construction. The alternative —
 * one `if (params?.x) searchParams.set('x', ...)` per field — drops anything the
 * builder doesn't explicitly know about, silently and with no type error. That
 * is not hypothetical: `journalEntryApi.list` was given `sort_by`/`sort_direction`
 * by the journal-entries list page, the page's own request-shape test passed (it
 * mocks the hook, a layer above the URL), and the params never left the browser —
 * the server kept applying its default sort behind a table that looked sorted.
 *
 * Falsy handling is deliberate and differs from a truthy `if`:
 * - `undefined` / `null` / `''` are skipped — "not filtering by this".
 * - `0` and `false` ARE sent. `offset=0` is page 1, and `include_inactive=false`
 *   is a real instruction, not the absence of one. A truthy check conflates the
 *   two and is how an explicit page-1 offset used to vanish.
 * - An array becomes repeated keys (`ids=a&ids=b`), matching FastAPI's
 *   `list[T] = Query(...)`. An endpoint that wants a comma-joined *string*
 *   (e.g. `comparative-profit-loss?years=2024,2025`) must join at the call site.
 *
 * Returns `'?a=1&b=2'`, or `''` when nothing survives — so callers can
 * concatenate it onto a path unconditionally.
 */
// `object` is intentional in the union: concrete request interfaces (e.g.
// `ArAgingParams`) lack a string index signature, so they don't structurally
// satisfy `Record<string, unknown>`. `object` accepts both shapes, and
// `Object.entries` narrows to string keys.
export function buildParams(params?: Record<string, unknown> | object): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value) sp.append(key, String(v));
    } else {
      sp.append(key, String(value));
    }
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}
